import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import 'iconify-icon';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useGlobalWaLink } from '../contexts/SettingsContext';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const CATEGORIES = [
  'Carreira',
  'Eneagrama',
  'Relacionamentos',
  'Engenharia Comportamental',
  'Empresas',
];

const BlogPage = () => {
  const { t } = useTranslation();
  const WA_LINK = useGlobalWaLink();
  const [posts, setPosts] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API_URL}/public/blog`);
        setPosts(Array.isArray(data) ? data : []);
      } catch (e) {
        console.warn('Error loading blog:', e?.message);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const filteredPosts =
    selectedCategory === 'all'
      ? posts
      : posts.filter((p) => p.category === selectedCategory);

  return (
    <div>
      <Header />
      <main className="pt-20">
        {/* Hero Section */}
        <section
          className="relative z-10 py-24 border-b border-neutral-900"
          style={{ background: 'rgba(10, 10, 10, 0.78)' }}
        >
          <div className="relative z-10 max-w-[1440px] mx-auto px-6 md:px-12">
            <div className="text-center max-w-3xl mx-auto">
              <span className="text-xs font-mono uppercase tracking-[0.3em] text-accent-orange mb-4 block">
                {t('pages.blog.hero_label')}
              </span>
              <h1 className="text-4xl md:text-6xl font-bold tracking-tight mb-6 text-[#EAEAEA] leading-[1.15] pb-1">
                {t('pages.blog.title')}
              </h1>
              <p className="text-lg text-neutral-400">
                {t('pages.blog.subtitle')}
              </p>
            </div>
          </div>
        </section>

        {/* Filters */}
        <section
          className="relative z-10 py-8 border-b border-neutral-900"
          style={{ background: 'rgba(17, 17, 17, 0.85)' }}
        >
          <div className="relative z-10 max-w-[1440px] mx-auto px-6 md:px-12">
            <div
              className="flex flex-wrap justify-center gap-3"
              data-testid="blog-category-filters"
            >
              <button
                onClick={() => setSelectedCategory('all')}
                data-testid="blog-cat-all"
                className={`px-6 py-3 rounded-full text-xs font-mono uppercase tracking-widest transition-colors ${
                  selectedCategory === 'all'
                    ? 'bg-accent-orange text-white'
                    : 'bg-[#1A1A1A] border border-[#333333] text-neutral-400 hover:border-accent-orange hover:text-accent-orange'
                }`}
              >
                {t('pages.blog.all_categories')}
              </button>
              {CATEGORIES.map((category) => (
                <button
                  key={category}
                  onClick={() => setSelectedCategory(category)}
                  data-testid={`blog-cat-${category.toLowerCase().replace(/\s+/g, '-')}`}
                  className={`px-6 py-3 rounded-full text-xs font-mono uppercase tracking-widest transition-colors ${
                    selectedCategory === category
                      ? 'bg-accent-orange text-white'
                      : 'bg-[#1A1A1A] border border-[#333333] text-neutral-400 hover:border-accent-orange hover:text-accent-orange'
                  }`}
                >
                  {category}
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* Posts Grid */}
        <section
          className="relative z-10 py-24"
          style={{ background: 'rgba(10, 10, 10, 0.78)' }}
        >
          <div className="relative z-10 max-w-[1440px] mx-auto px-6 md:px-12">
            {loading ? (
              <div className="text-center py-16 text-neutral-500">
                Carregando publicações…
              </div>
            ) : filteredPosts.length === 0 ? (
              <div
                className="text-center py-20"
                data-testid="blog-empty-state"
              >
                <iconify-icon
                  icon="solar:document-text-linear"
                  className="text-6xl text-accent-orange mb-4"
                ></iconify-icon>
                <h2 className="text-2xl md:text-3xl font-bold text-[#EAEAEA] mb-3">
                  {t('pages.blog.no_articles')}
                </h2>
                <p className="text-neutral-400 max-w-md mx-auto mb-6">
                  Enquanto isso, fale com nossa equipe e descubra como o método
                  IE intervém na estrutura comportamental que sustenta seus
                  padrões automáticos.
                </p>
                <a
                  href={WA_LINK}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="blog-empty-whatsapp-cta"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-accent-orange hover:bg-[#E55D00] text-white rounded-full text-xs font-mono uppercase tracking-widest transition-colors"
                >
                  <iconify-icon icon="mdi:whatsapp"></iconify-icon>
                  Falar com a equipe
                </a>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {filteredPosts.map((post) => (
                  <Link
                    to={`/blog/${post.slug}`}
                    key={post.post_id}
                    data-testid={`blog-card-${post.slug}`}
                    className="glow-card bg-[#1A1A1A] border border-[#333333] rounded-xl overflow-hidden hover-lift cursor-pointer block"
                  >
                    {post.featured_image ? (
                      <div className="aspect-video bg-[#1F1F1F] overflow-hidden">
                        <img
                          src={post.featured_image}
                          alt={post.title}
                          className="w-full h-full object-cover"
                        />
                      </div>
                    ) : (
                      <div className="aspect-video bg-gradient-to-br from-[#1A1A1A] via-[#0F0F0F] to-[#1A1A1A] flex items-center justify-center px-6 text-center">
                        <p className="text-[#EAEAEA] text-base font-bold line-clamp-3 tracking-tight">
                          {post.title}
                        </p>
                      </div>
                    )}
                    <div className="p-6">
                      <div className="flex items-center gap-2 text-xs text-neutral-400 mb-3">
                        <span className="px-2 py-1 bg-accent-orange/20 text-accent-orange rounded-full uppercase tracking-wider font-mono">
                          {post.category}
                        </span>
                        <span>•</span>
                        <span>
                          {new Date(post.created_at).toLocaleDateString('pt-BR')}
                        </span>
                      </div>
                      <h2 className="text-xl font-bold mb-3 line-clamp-2 text-[#EAEAEA]">
                        {post.title}
                      </h2>
                      <p className="text-sm text-neutral-400 mb-4 line-clamp-3">
                        {post.excerpt}
                      </p>
                      <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-accent-orange">
                        {t('pages.blog.read_article')}
                        <iconify-icon icon="solar:arrow-right-linear"></iconify-icon>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default BlogPage;
